<template>
  <Modal :open="open" @close="$emit('close')">
    <div class="space-y-6">
      <div class="text-center">
        <h2 class="text-lg font-semibold text-gray-900">{{ t("title") }}</h2>
        <p class="mt-1 text-sm text-gray-500">{{ t("description") }}</p>
      </div>

      <div v-if="hasActiveFilters" class="space-y-3">
        <p class="text-sm font-medium text-gray-700">{{ t("active_filters") }}</p>
        <div class="flex flex-wrap gap-2">
          <template v-for="(values, filterName) in activeFilters" :key="filterName">
            <span
              v-for="value in values"
              :key="`${filterName}-${value}`"
              class="inline-flex items-center rounded-full bg-indigo-50 px-2.5 py-0.5 text-xs font-medium text-indigo-700"
            >
              {{ filterLabel(filterName, value) }}
            </span>
          </template>
        </div>
      </div>
      <div v-else>
        <p class="text-sm text-gray-500">{{ t("no_filters") }}</p>
      </div>

      <div class="flex justify-end space-x-4">
        <SecondaryButton @click="$emit('close')">{{ t("cancel_button") }}</SecondaryButton>
        <GenericButton :disabled="exporting" @click="exportClaims">
          {{ t("export_button") }}
        </GenericButton>
      </div>
    </div>
  </Modal>
</template>

<script>
import { useI18n } from "vue-i18n";
import { Modal, GenericButton, SecondaryButton } from "@estaly/ui";
import { mapActions } from "vuex";

const EXPORT_FILTER_KEYS = [
  "status",
  "organizations",
  "stores",
  "category",
  "claimType",
  "insurerContractGroupReference",
];

export default {
  components: { Modal, GenericButton, SecondaryButton },

  props: {
    open: { type: Boolean, required: true },
    selectedFilters: { type: Object, required: true },
    availableFilters: { type: Object, required: true },
  },

  emits: ["close"],

  setup() {
    const { t } = useI18n();
    return { t };
  },

  data() {
    return {
      exporting: false,
    };
  },

  computed: {
    activeFilters() {
      const filters = {};
      for (const key of EXPORT_FILTER_KEYS) {
        const values = this.selectedFilters[key];
        if (values && values.length > 0) {
          filters[key] = values;
        }
      }
      return filters;
    },

    hasActiveFilters() {
      return Object.keys(this.activeFilters).length > 0;
    },
  },

  methods: {
    ...mapActions("claims", ["bulkExportClaims"]),
    ...mapActions("notifications", ["notify"]),

    filterLabel(filterName, value) {
      const options = this.availableFilters[filterName] || [];
      const option = options.find((o) => o.value === value);
      if (!option) return value;

      const label = option.label;
      if (label.startsWith("claim.")) {
        return this.$t(label);
      }
      return label;
    },

    async exportClaims() {
      this.exporting = true;

      const response = await this.bulkExportClaims(this.activeFilters);

      this.exporting = false;

      if (!response?.success) return;

      this.notify({
        category: "simple",
        type: "success",
        title: this.t("export_success_title"),
        text: this.t("export_success"),
      });
      this.$emit("close");
    },
  },
};
</script>

<i18n src="./BulkExportClaimsModal.i18n.json"></i18n>
